<template>
  <v-layout>
    <v-flex>
      <v-layout wrap align-center>
        <h1 class="headline">Translations</h1>
        <v-spacer></v-spacer>
        <v-flex xs12 sm4 class="pb-2">
          <v-text-field
            append-icon="mdi-magnify"
            label="Quick Filter..."
            single-line
            hide-details
            v-model="search"
            class="ma-0"
            :outlined="outlinedInputs"
          ></v-text-field>
        </v-flex>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              v-on="on"
              fab
              dark
              color="primary"
              @click="createItem"
              class="ma-2"
            >
              <v-icon dark>mdi-plus</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </v-layout>
      <v-card>
        <div class="translationsTable">
          <v-data-table
            :headers="headers"
            :items="rows"
            :loading="$apollo.loading"
            v-bind="pagingOptions"
            :search="search"
            item-key="node.id"
          >
            <template v-slot:body="{ items }">
              <tbody>
                <tr v-for="item in items" :key="item.id">
                  <td class="text-xs-left greyBg" nowrap>
                    <v-edit-dialog
                      lazy
                      large
                      @save="onSaveTranslationLabel(item.label, item.key)"
                    >
                      <b>{{ item.label }}</b>
                      <template v-slot:input>
                        <v-text-field
                          v-model="item.label"
                          label="Edit"
                          single-line
                        ></v-text-field>
                      </template>
                    </v-edit-dialog>
                  </td>
                  <td
                    v-for="header in textHeaders"
                    :key="header.text"
                    class="text-xs-left"
                  >
                    <confirmation-dialog
                      v-if="header.name == 'actions'"
                      @onConfirm="onRemoveAccept(item.key)"
                    >
                      <v-btn icon small class="mx-0">
                        <v-icon small color="pink">mdi-delete</v-icon>
                      </v-btn>
                      <template slot="message"
                        >This will remove all translations for this
                        label.</template
                      >
                    </confirmation-dialog>
                    <v-edit-dialog
                      v-else
                      :return-value.sync="item[header.text]"
                      lazy
                      large
                      @save="onSaveLabel(item, header)"
                    >
                      {{ item[header.text] }}
                      <template v-slot:input>
                        <v-text-field
                          v-model="item[header.text]"
                          label="Edit"
                          single-line
                        ></v-text-field>
                      </template>
                    </v-edit-dialog>
                  </td>

                  <td
                    v-if="false"
                    v-for="header in textHeaders"
                    :key="header.text"
                    @mouseover="onHover(item.label, header.text)"
                    class="text-xs-left"
                  >
                    <translations-item-actions
                      :item="selectedTranslation"
                      @editItem="editItem"
                      @deleteItem="deleteItem"
                    >
                      <template v-slot:translation>{{
                        item[header.text]
                      }}</template>
                    </translations-item-actions>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </div>
      </v-card>

      <v-dialog v-model="translationEditorDialog" persistent width="500">
        <v-card>
          <translation-editor
            :translation="selectedTranslation"
            :translationEditorDialog="translationEditorDialog"
            @closeDialog="closeDialog"
            @saved="onSaved($event)"
          ></translation-editor>
        </v-card>
      </v-dialog>
    </v-flex>
    <v-row v-if="debug">
      <code>{{ translations }}</code>
    </v-row>
  </v-layout>
</template>

<script>
import {
  ALL_LANGUAGES_SEARCH_QUERY,
  ALL_TRANSLATIONS_SEARCH_QUERY,
  TRANSLATION_CREATE_MUTATION,
  TRANSLATION_UPDATE_MUTATION,
  TRANSLATION_DELETE_MUTATION,
} from "../../../../constants/graphql";
import DefaultListActions from "../../shared/DefaultListActions";
import TranslationsItemActions from "./TranslationsItemActions";
import TranslationEditor from "./TranslationEditor";
import { pagingOptions } from "../../../../helpers/index";
import gql from "graphql-tag";
import ConfirmationDialog from "../../shared/ConfirmationDialog.vue";

const UPDATE_TRANSLATIONS_BY_LABEL = gql`
  mutation updateTranslationsLabel($label: String!, $value: String!) {
    updateTranslationsLabel(label: $label, value: $value) {
      error
      success
      message
    }
  }
`;
const DELETE_TRANSLATTIONS_BY_LABEL = gql`
  mutation deleteTranslationsByLabel($label: String!) {
    deleteTranslationsByLabel(label: $label) {
      success
      error
      message
    }
  }
`;

export default {
  name: "TranslationsList",
  components: {
    TranslationsItemActions,
    DefaultListActions,
    TranslationEditor,
    ConfirmationDialog,
  },
  data() {
    return {
      debug: false,
      pagingOptions,
      languages: null,
      translations: null,
      modifyTranslationFab: false,
      selectedTranslation: {},
      search: "",
      rows: [],
      headers: [],
      doSaveTranslation: false,
      translationEditorDialog: false,
      defaultSort: {
        sortBy: "label",
      },
    };
  },

  computed: {
    textHeaders() {
      return this.headers.filter((header) => {
        return header.text != "Label";
      });
    },
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
  },

  methods: {
    onRemoveAccept(label) {
      console.log("removing: ", label);
      this.$apollo
        .mutate({
          mutation: DELETE_TRANSLATTIONS_BY_LABEL,
          variables: { label },
          refetchQueries: [
            {
              query: ALL_TRANSLATIONS_SEARCH_QUERY,
            },
          ],
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Translation Label Removed.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    onSaveTranslationLabel(value, label) {
      this.$apollo
        .mutate({
          mutation: UPDATE_TRANSLATIONS_BY_LABEL,
          variables: { label, value },
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Translation Label Updated.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
    initializeHeaders(data) {
      let labelHeader = {};
      labelHeader["text"] = "Label";
      labelHeader["align"] = "left";
      labelHeader["value"] = "label";
      labelHeader["name"] = "label";
      this.headers.push(labelHeader);
      data.edges.map((item) => {
        let newHeader = {};
        newHeader["text"] = item.node.name;
        newHeader["align"] = "left";
        newHeader["value"] = item.node.name;
        newHeader["name"] = item.node.name;
        newHeader.id = item.node.id;
        this.headers.push(newHeader);
      });
      this.headers.push({
        text: "Actions",
        align: "center",
        value: "actions",
        name: "actions",
      });
    },
    initializeRows(data) {
      this.rows = [];
      let translations = data.edges;
      for (let i = 0; i < translations.length; i++) {
        let labelObject = this.rows.filter((item) => {
          return item.label == translations[i].node.label;
        })[0];
        let languages = this.languages.edges;
        if (labelObject == null) {
          let newRow = {};
          newRow.label = translations[i].node.label;
          newRow.key = translations[i].node.label;

          if (translations[i].node.language != null) {
            for (let j = 0; j < languages.length; j++) {
              newRow[languages[j].node.name] =
                translations[i].node.language.id === languages[j].node.id
                  ? translations[i].node.translation
                  : "";
            }
          }
          this.rows.push(newRow);
        } else {
          if (translations[i].node.language != null) {
            let translationLanguage = languages.filter((lang) => {
              return lang.node.id === translations[i].node.language.id;
            })[0];
            labelObject[translationLanguage.node.name] =
              translations[i].node.translation;
          }
        }
      }
    },
    onHover(translationLabel, headerText) {
      let selectedLanguage = this.languages.edges.filter((item) => {
        return item.node.name === headerText;
      })[0];

      let selectedTranslation = this.translations.edges.filter((item) => {
        if (item.node.language != null) {
          return (
            item.node.label === translationLabel &&
            item.node.language.id === selectedLanguage.node.id
          );
        }
      })[0];

      if (selectedTranslation != null) {
        this.selectedTranslation = selectedTranslation.node;
      } else {
        this.selectedTranslation = {};
        this.selectedTranslation["language"] = selectedLanguage.node;
        this.selectedTranslation["label"] = translationLabel;
      }
    },
    editItem() {
      this.translationEditorDialog = true;
    },
    deleteItem() {
      this.$apollo
        .mutate({
          mutation: TRANSLATION_DELETE_MUTATION,
          variables: {
            translationId: this.selectedTranslation.id,
          },
        })
        .then(this.removeGqlObjectHandler, this.removeGqlObjectErrorHandler);
    },
    removeGqlObjectHandler(result) {
      this.$store.dispatch("showAppSnackbar", { msg: "Translation removed." });
      this.selectedTranslation = {};

      if (this.$apollo.queries.translations != null) {
        this.$apollo.queries.translations.refetch();
      }
    },
    removeGqlObjectErrorHandler(err) {
      this.$store.dispatch("showAppSnackbar", {
        msg: err.graphQLErrors[0].message,
        color: "error",
      });
    },
    onSaved(context) {
      if (this.$apollo.queries.translations != null) {
        this.$apollo.queries.translations.refetch();
      }
      this.translationEditorDialog = false;
      this.selectedTranslation = {};
      let snackMsg =
        context == "edit" ? "Translation updated." : "Translation created.";
      this.$store.dispatch("showAppSnackbar", { msg: snackMsg });
    },
    createItem() {
      this.selectedTranslation = {};
      this.translationEditorDialog = true;
    },
    closeDialog() {
      this.translationEditorDialog = false;
      this.selectedTranslation = {};
    },
    onSaveLabel(item, lang) {
      const newVal = item[lang.name];
      const editedTranslation = this.translations.edges.find(
        (t) =>
          t.node.language &&
          t.node.language.id == lang.id &&
          t.node.label == item.label
      );
      const input = {
        label: item.label,
        translation: newVal,
        languageId: lang.id,
      };
      if (editedTranslation) {
        input.id = editedTranslation.node.id;
      }

      this.$apollo
        .mutate({
          mutation: editedTranslation
            ? TRANSLATION_UPDATE_MUTATION
            : TRANSLATION_CREATE_MUTATION,
          variables: input,
        })
        .then(() => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Translation Saved.",
          });
        })
        .catch((err) => {
          const errorMsg = err.graphQLErrors.map((e) => e.message).join(", ");
          this.$store.dispatch("showAppSnackbar", {
            msg: errorMsg,
            color: "error",
          });
        });
    },
  },

  watch: {
    languages(data) {
      this.initializeHeaders(data);
      if (this.translations != null) {
        this.initializeRows(this.translations);
      }
    },
    translations(data) {
      if (this.languages != null) {
        this.initializeRows(data);
      }
    },
  },

  apollo: {
    languages: {
      query: ALL_LANGUAGES_SEARCH_QUERY,
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {},
    },
    translations: {
      query: ALL_TRANSLATIONS_SEARCH_QUERY,
      fetchPolicy: "network-only",
      result({ data, loader, networkStatus }) {},
    },
  },
};
</script>

<style scoped>
.translationsTable td:hover {
  background-color: #eee;
}

.greyBg {
  background-color: #eee;
}

.translationsTable table.v-table tbody td:not(:first-child) {
  padding: 0 0 0 12px;
}

.translationsTable table.v-table thead th:not(:first-child) {
  padding: 0 0 0 12px;
}

.translationsTable td {
  position: relative;
  min-width: 88px;
}

.translationsTable {
  border-collapse: collapse;
}

.translationsTable th:first-child {
  background-color: #eee;
}

.translationsTable tr:hover:not(.v-datatable__expand-row) {
  background: #fff !important;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/admin/languages/TranslationsList.vue